import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import { useFeedback } from 'contexts/FeedbackContext';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useUpdateEditableAttributesMutation } from 'services/api/clients.api';
import type { Client } from 'services/types/client';
import { NAVY, RADIUS_COIN, RADIUS_ITEM, SURFACE_CONTAINER_LOWEST, YELLOW } from 'styles/tokens';

interface Props {
  client: Client;
}

const ClientNotesEditor: React.FC<Props> = ({ client }) => {
  const [updateClient, { isLoading }] = useUpdateEditableAttributesMutation();
  const [draft, setDraft] = useState(client.notes ?? '');
  const prevNotesRef = useRef(client.notes);
  const { notifySaved } = useFeedback();

  useEffect(() => {
    if (client.notes !== prevNotesRef.current) {
      setDraft(client.notes ?? '');
      prevNotesRef.current = client.notes;
    }
  }, [client.notes]);

  const hasChanges = draft !== (client.notes ?? '');

  const handleSave = async () => {
    try {
      await updateClient({ id: client.id, body: { notes: draft || null } }).unwrap();
      notifySaved('Notas guardadas');
    } catch (error) {
      console.error('Error saving notes:', error);
    }
  };

  return (
    <Box>
      <TextField
        multiline
        minRows={5}
        maxRows={12}
        fullWidth
        size="small"
        placeholder="Escribe una nota sobre este cliente..."
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        disabled={isLoading}
        sx={{
          '& .MuiOutlinedInput-root': {
            backgroundColor: SURFACE_CONTAINER_LOWEST,
            borderRadius: RADIUS_ITEM,
            fontSize: '0.875rem',
            color: NAVY,
            '& fieldset': {
              border: 'none',
            },
            '&:hover fieldset': {
              border: 'none',
            },
            '&.Mui-focused fieldset': {
              border: 'none',
            },
          },
        }}
      />
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          mt: 1,
          gap: 1,
        }}
      >
        <Button
          variant="contained"
          size="small"
          onClick={handleSave}
          disabled={isLoading || !hasChanges}
          startIcon={isLoading ? <CircularProgress size={14} sx={{ color: NAVY }} /> : undefined}
          sx={{
            backgroundColor: YELLOW,
            color: NAVY,
            borderRadius: RADIUS_COIN,
            fontWeight: 600,
            textTransform: 'none',
            minWidth: 132,
            '&:hover': {
              backgroundColor: YELLOW,
              opacity: 0.9,
            },
            '&.Mui-disabled': {
              backgroundColor: YELLOW,
              color: NAVY,
              opacity: 0.6,
            },
          }}
        >
          {isLoading ? 'Guardando…' : 'Guardar notas'}
        </Button>
      </Box>
    </Box>
  );
};

export default ClientNotesEditor;
